





























import { Component, Prop, Mixins } from "vue-property-decorator";
import { mapState } from "vuex";

// Mixins
import RequestPromises from "@/mixins/requestPromises";

// Enums/Data
import { DataModules } from "@/store/modules/data";

// Typings
import { IState } from "@/store";
import { IProduct, IProductMigration } from "@/models/products";

@Component({
  components: {
    "multilevel-categories-selections": () =>
      import(
        "@/components/app/admin/catalogue/products/multilevelCategories/multilevelCategoriesSelections.vue"
      )
  },
  computed: {
    ...mapState({
      productScope(state: IState, getters) {
        return getters[`data/${DataModules.CATALOGUE}/scope`](this.productId);
      },
      product(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.CATALOGUE,
          scope: this.productScope
        });
      }
    })
  }
})
export default class EditProductMigrations extends Mixins(RequestPromises) {
  // State
  public product!: IProduct;
  public productScope!: string;

  // Props
  @Prop({ required: true })
  public productId!: IProduct["id"];

  // Data
  public isLoading = false;
  public isReloading = false;
  public requestError = false;
  public isProcessing = false;

  // Methods
  public created() {
    if (!this.contentIsAvailable) return;

    this.isLoading = true;
    this.loadData();
  }

  public async loadData() {
    try {
      await this.getProductData(this.isReloading);
      this.requestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    } finally {
      this.isReloading = false;
      this.isLoading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public getProductData(ignoreStored = false) {
    return this.$store.dispatch(`data/${DataModules.CATALOGUE}/get`, {
      id: this.productId,
      scope: this.productScope,
      vm: this,
      ignoreStored: ignoreStored
    });
  }

  public attachAllowedMigrations(productIds: IProduct["id"][]) {
    return this.$store.dispatch(
      `data/${DataModules.CATALOGUE}/attachAllowedMigrations`,
      {
        productId: this.productId,
        data: {
          migration_product_ids: productIds
        }
      }
    );
  }

  public detachAllowedMigration(migrationProductId: IProduct["id"]) {
    return this.$store.dispatch(
      `data/${DataModules.CATALOGUE}/detachAllowedMigration`,
      {
        productId: this.productId,
        migrationProductId
      }
    );
  }

  public get allowedMigrations() {
    return this.$_.orderBy(
      this.$_.map(this.$_.get(this.product, "allowed_migrations", [])),
      ["order", "asc"]
    );
  }

  public get selectedProducts() {
    return this.$_.map(
      this.allowedMigrations,
      (allowedMigration: IProductMigration) => {
        return {
          id: allowedMigration.migration_product_id,
          category_id: this.$_.get(
            allowedMigration,
            "migration_product.category_id"
          ),
          type: "item",
          name: this.$_.get(allowedMigration, "migration_product.name")
        };
      }
    );
  }

  public async detachItem(itemId: IProduct["id"]) {
    this.isProcessing = true;

    try {
      await this.detachAllowedMigration(
        this.$_.get(
          this.$_.find(this.allowedMigrations, [
            "migration_product_id",
            itemId
          ]),
          "id",
          ""
        )
      );
      await this.getProductData(true);
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    } finally {
      this.isProcessing = false;
    }
  }

  public async onSelectedProducts(selectedProducts) {
    this.isProcessing = true;
    try {
      const newMigrationsIds = this.$_.map(
        this.$_.filter(
          selectedProducts,
          selectedProduct =>
            !this.$_.find(this.allowedMigrations, [
              "migration_product_id",
              selectedProduct.id
            ])
        ),
        product => product.id
      );

      if (newMigrationsIds.length) {
        await this.attachAllowedMigrations(newMigrationsIds);
      }

      const oldMigrationsIds = this.$_.map(
        this.$_.filter(
          this.allowedMigrations,
          (allowedMigration: any) =>
            !this.$_.find(selectedProducts, [
              "id",
              allowedMigration.migration_product_id
            ])
        ),
        product => product.id
      );

      if (oldMigrationsIds.length) {
        await Promise.all(
          this.$_.map(oldMigrationsIds, oldMigrationsId =>
            this.detachAllowedMigration(oldMigrationsId)
          )
        );
      }

      await this.getProductData(true);
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    } finally {
      this.isProcessing = false;
    }
  }

  public async reorder(movedElement: any) {
    this.isProcessing = true;

    const movedMigrationId = this.$_.get(
      this.$_.find(this.allowedMigrations, [
        "migration_product_id",
        this.$_.get(movedElement, "itemMoved.id")
      ]),
      "id"
    );

    const newOrder = this.$_.filter(
      this.$_.map(
        this.allowedMigrations,
        (allowedMigration: any) => allowedMigration.id
      ),
      migrationId => migrationId !== movedMigrationId
    );

    newOrder.splice(this.$_.get(movedElement, "newIndex"), 0, movedMigrationId);

    try {
      await this.reorderAllowedMigrations(newOrder);
      await this.getProductData(true);
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    } finally {
      this.isProcessing = false;
    }
  }

  public reorderAllowedMigrations(order) {
    return this.$store.dispatch(
      `data/${DataModules.CATALOGUE}/reorderAllowedMigrations`,
      {
        id: this.productId,
        data: {
          migration_ids: order
        }
      }
    );
  }

  public get canManageProduct() {
    return this.$userCan("update_product");
  }

  public guardFunctionalities = ["staff_get_product"];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  public beforeDestroy() {
    this.$store.dispatch("data/bin", {
      storeModule: DataModules.CATALOGUE,
      scope: this.productScope,
      vm: this
    });
  }
}
